/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PasswordInput } from '@/components/blocks/PasswordInput';
import { loginButtonSX } from '@/components/content/Login/styles/button';
import { UserLogon } from '@/data/Content/Login';
import { useResendUserActivationCallback } from '@/data/Content/ResendUserActivationCallback';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { ADDRESS_FIELD_LENGTH } from '@/data/constants/addressFields';
import { useForm } from '@/utils/useForm';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';

import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { emailActivationContainerSX } from './styles/container';

const initLoginValues: UserLogon = {
	logonId: '',
	logonPassword: '',
	rememberMe: false,
};
type Props = {
	onSubmit: (value: UserLogon) => void;
};

export const ResendActivationEmail: FC<{ id: ID }> = () => {
	const RouteLocal = useLocalization('Routes');
	const activateEmailNLS = useLocalization('ResendActivationEmail');
	const [recurringMessage, setRecurringMessage] = useState<string>('');
	const { resendUserActivationCallBackAndRedirect } = useResendUserActivationCallback();
	const {
		handleInputChange,
		values: loginValues,
		formRef,
		error,
		handleSubmit,
		submitting,
		onNamedValueChange
	} = useForm(initLoginValues);

	const router = useNextRouter();

	useEffect(() => {

		const logonIdEnc = router?.query?.krypto;

		if (logonIdEnc) {
			const logonidDec = window.atob(logonIdEnc.toString());
			const logonid = logonidDec.split("=")[1];
			onNamedValueChange('logonId', logonid);

		}
	}, []);
	const {
		dimensions: { contentSpacing },
	} = useTheme();

	const handleLogin = async (props: UserLogon) => {
		const resp = await resendUserActivationCallBackAndRedirect(props);
	};
	return (
		<Grid container spacing={contentSpacing} justifyContent="center">
			<Grid item xs={12} sm={8} md={6} m="auto">
				<Paper sx={emailActivationContainerSX}>
					<Stack spacing={2} component="form" onSubmit={handleSubmit(handleLogin)} noValidate ref={formRef}>
						<Typography variant="h4">{activateEmailNLS.Title.t()}</Typography>
						<Typography variant="h3" sx={{ marginTop: 2 }}>
							{activateEmailNLS.Header1.t()}
						</Typography>
						<Typography variant="body1" sx={{ marginTop: 2 }}>
							{activateEmailNLS.Header2.t()}
						</Typography>
						{recurringMessage ? (
							<Alert variant="standard" severity="warning">
								{recurringMessage}
							</Alert>
						) : null}
						<TextField
							variant="outlined"
							margin="normal"
							required
							name="logonId"
							autoComplete="username"
							label={activateEmailNLS.Label.LogonId.t()}
							autoFocus
							value={loginValues.logonId}
							onChange={handleInputChange}
							inputProps={{ maxLength: ADDRESS_FIELD_LENGTH.logonId }}
							error={error.logonId}
						/>
						<PasswordInput
							required
							name="logonPassword"
							label={activateEmailNLS.Label.Password.t()}
							value={loginValues.logonPassword}
							onChange={handleInputChange}
							error={error.logonPassword}
							inputProps={{
								maxLength: 100,
							}}
						/>
						<Box>
							<FormControlLabel
								control={
									<Checkbox
										name="rememberMe"
										checked={loginValues.rememberMe}
										onChange={handleInputChange}
									/>
								}
								label={activateEmailNLS.Label.rememberMe.t()}
							/>
						</Box>

						<Stack spacing={2} >
							<Stack spacing={2} direction="row" justifyContent="center">
								<Stack>
									<Button
										type="submit"
										variant="contained"
										sx={loginButtonSX}
										disabled={submitting}
										data-testid="button-sign-in-submit"
										id="button-sign-in-submit"
									>
										{activateEmailNLS.SendMailButton.t()}
									</Button>
								</Stack>
							</Stack>
						</Stack>
					</Stack>
				</Paper>
			</Grid>
		</Grid>
	);
};
